/**
 * Display helpers for the Reservations tab (BON-382). Pure, so the awkward
 * cases are unit-testable without rendering anything.
 */
import type { ReservationStage } from '../../api/reservations';
import type { StatusTone } from '../dashboards/primitives';

/**
 * The tones a reservation stage can carry — a subset of the app's status
 * vocabulary, so `StatusPill` renders one directly (BON-478).
 *
 * Semantic rather than presentational: two surfaces read this with different
 * styling systems (Tailwind on the bookings page, `nb-*` on workshop detail), so
 * a colour-named tone could serve only one of them.
 */
export type ReservationTone = Extract<StatusTone, 'watch' | 'open' | 'at-risk' | 'active' | 'soft'>;

/**
 * How a stage READS. Deliberately not whether anything can be done to it:
 * whether the seats can come back depends on what the row is holding as well as
 * on its stage, so that question lives with the other action rules in
 * `reservationRotationRules` and is asked with the row in hand (BON-472). A
 * stage-keyed copy of it here is how Release came to be offered on rows that
 * hold nothing.
 */
export interface StageBadge {
  label: string;
  tone: ReservationTone;
}

export function stageBadge(stage: ReservationStage): StageBadge {
  switch (stage) {
    case 'link_sent':
      return { label: 'Link sent', tone: 'watch' };
    case 'engaged':
      return { label: 'In checkout', tone: 'open' };
    case 'card_declined':
      // One word on purpose. "Card declined" wrapped onto two lines inside the
      // pill in the Stage column, which is narrow and shares its cell with the
      // CSR's name. The stage vocabulary has to fit that width.
      return { label: 'Declined', tone: 'at-risk' };
    case 'attempts_exhausted':
      // Terminal (BON-472): the checkout is `DECLINED` and cannot take money
      // again. THE SEATS ARE ALREADY BACK — the strike-out releases the hold in
      // the same breath as writing the status, so nothing is waiting on a CSR.
      //
      // `soft`, like the other finished stages, on the operator's call of
      // 2026-08-24. An earlier version painted this `at-risk` on the grounds
      // that the CUSTOMER is still unresolved and worth a call — true, but the
      // ROW is inert, and red on an inert row teaches a CSR to discount red on
      // a list where red has to mean act now. Following up an unresolved
      // customer is a different job from working this queue.
      return { label: 'Out of attempts', tone: 'soft' };
    case 'paying':
      return { label: 'Paying', tone: 'active' };
    case 'needs_review':
      return { label: 'Needs review', tone: 'at-risk' };
    case 'expired':
      return { label: 'Expired', tone: 'soft' };
    case 'released':
      return { label: 'Released', tone: 'soft' };
  }
}

/**
 * Coarse time-to-expiry. No ticking countdown, by decision: a table of seconds
 * counting down is more movement than information, and these run from minutes
 * to two days.
 */
export function expiresInLabel(expiresAt: string, now: Date = new Date()): string {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'expired';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 5) return '<5m';
  if (minutes < 10) return '<10m';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  return `${Math.floor(hours / 24)}d`;
}

/**
 * How long this reservation has been sitting, coarse (BON-478). design.md names
 * AGE as the mitigation for one that quietly never moves; expiry alone cannot
 * show it. Same coarseness as {@link expiresInLabel}.
 */
export function ageLabel(createdAt: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(createdAt).getTime();
  if (Number.isNaN(ms)) return '—';
  // Clock skew reads as new, never as a negative age.
  if (ms <= 0) return 'just now';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  return `${Math.floor(hours / 24)}d`;
}

export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/** Venue wall-clock, read literally — the stored value is already local, so it
 *  must not be shifted by the viewer's zone. */
export function formatWorkshopWhen(iso: string | null): string {
  if (!iso) return '';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(when);
}
